// Contrail V3.11.0 — platform notices for users: a banner on every app page and a box on the sign-in page.
// Content comes from /api/status (Netlify), so it shows even when Supabase is down. Users can hide a notice;
// it then shrinks to a small "Notice" chip (and comes back by itself if the notice is changed).
import { useState } from "react";
import { useStatusNotices } from "../lib/useStatusNotices";
import { LEVEL_LABEL, phase, windowText, type PublicNotice } from "../lib/statusNotice";

const HIDDEN = "fw.status.hidden";
const readHidden = (): Record<string, string> => { try { return JSON.parse(localStorage.getItem(HIDDEN) ?? "{}") ?? {}; } catch { return {}; } };
const saveHidden = (h: Record<string, string>) => { try { localStorage.setItem(HIDDEN, JSON.stringify(h)); } catch { /* storage off */ } };

/** Colour/wording key: upcoming = blue "Planned", otherwise by level. */
function tone(n: PublicNotice) {
  const p = phase(n);
  if (p === "upcoming") return { cls: "sn-plan", label: n.level === "info" ? "Notice" : `Planned · ${LEVEL_LABEL[n.level].replace(/ in progress$/, "")}` };
  return { cls: n.level === "issue" ? "sn-issue" : n.level === "info" ? "sn-info" : "sn-work", label: LEVEL_LABEL[n.level] };
}

function Body({ n }: { n: PublicNotice }) {
  const tn = tone(n);
  const when = windowText(n);
  return (
    <div className="sn-body">
      <span className="sn-tag">{tn.label}</span>
      <b className="sn-title">{n.title}</b>
      <span className="sn-msg">{n.message}</span>
      {when && <span className="sn-when">{when}</span>}
    </div>
  );
}

/** Same look as users see it (Admin → Status preview). */
export function NoticePreview({ n }: { n: PublicNotice }) {
  return <div className={`sn ${tone(n).cls}`}><Body n={n} /></div>;
}

/** Banner on every page except sign-in: floats over the map (desktop map, phone app); a strip above the top bar elsewhere. */
export function StatusBanner({ mode = "strip" }: { mode?: "float" | "phone" | "strip" }) {
  const notices = useStatusNotices("app");
  const [hidden, setHidden] = useState(readHidden);
  if (!notices.length) return null;
  const open = notices.filter((n) => hidden[n.id] !== n.updated_at);
  const hide = (n: PublicNotice) => { const h = { ...readHidden(), [n.id]: n.updated_at }; saveHidden(h); setHidden(h); };
  const showAll = () => { const h = readHidden(); notices.forEach((n) => delete h[n.id]); saveHidden(h); setHidden({ ...h }); };
  return (
    <div className={`sn-wrap sn-${mode}`} role="status" aria-live="polite">
      {open.map((n) => (
        <div key={n.id} className={`sn ${tone(n).cls}`}>
          <Body n={n} />
          <button type="button" className="sn-hide" onClick={() => hide(n)} title="Hide this notice" aria-label="Hide this notice">×</button>
        </div>
      ))}
      {open.length < notices.length && (
        <button type="button" className={`sn-chip ${tone(notices[0]).cls}`} onClick={showAll} title="Show the platform notice">
          <span className="sn-dot" aria-hidden="true" />{notices.length - open.length === 1 ? "Notice" : `${notices.length - open.length} notices`}
        </button>
      )}
    </div>
  );
}

/** Inline box on the sign-in page (shown above the form, can't be hidden). */
export function StatusLoginBox() {
  const notices = useStatusNotices("login");
  if (!notices.length) return null;
  return (
    <div className="sn-login" role="status" aria-live="polite">
      {notices.map((n) => <div key={n.id} className={`sn ${tone(n).cls}`}><Body n={n} /></div>)}
    </div>
  );
}
